import React from "react";
import { createPortal } from "react-dom";
import { Bell, Mail, Plus } from "lucide-react";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

const MENU_WIDTH = 252;
const MENU_MARGIN = 8;
const MENU_HEADER_HEIGHT = 48;
const MENU_ITEM_HEIGHT = 40;

function menuPosition(position, itemCount) {
  const viewportWidth = typeof document === "undefined"
    ? MENU_WIDTH
    : document.documentElement.getBoundingClientRect().width || document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = typeof document === "undefined"
    ? MENU_HEADER_HEIGHT + itemCount * MENU_ITEM_HEIGHT
    : document.documentElement.clientHeight || window.innerHeight;
  const menuHeight = MENU_HEADER_HEIGHT + itemCount * MENU_ITEM_HEIGHT + MENU_MARGIN;
  return {
    left: Math.max(MENU_MARGIN, Math.min(position.x, viewportWidth - MENU_WIDTH - MENU_MARGIN)),
    top: Math.max(MENU_MARGIN, Math.min(position.y, viewportHeight - menuHeight - MENU_MARGIN)),
  };
}

export default function AutomationDateCreationMenu({
  canCreateEmail = false,
  dateLabel,
  onClose,
  onSelect,
  position,
}) {
  const menuRef = React.useRef(null);
  const actions = React.useMemo(() => [
    canCreateEmail ? { key: "reports.email", label: "Nouveau courriel", icon: Mail } : null,
    { key: "notification.in_app", label: "Nouvelle notification", icon: Bell },
    { key: "dispatch.optimize", label: "Nouvelle optimisation", icon: Plus },
  ].filter(Boolean), [canCreateEmail]);

  React.useEffect(() => {
    if (!dateLabel || !position) return undefined;
    menuRef.current?.querySelector("button")?.focus();
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) onClose?.();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    const handleViewportChange = () => onClose?.();
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [dateLabel, onClose, position]);

  if (!dateLabel || !position || actions.length === 0) return null;
  const portalRoot = getReactPortalRoot();
  if (!portalRoot) return null;
  const resolvedPosition = menuPosition(position, actions.length);

  const handleMenuKeyDown = (event) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = [...event.currentTarget.querySelectorAll("button:not(:disabled)")];
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement);
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? items.length - 1
        : event.key === "ArrowDown"
          ? (currentIndex + 1 + items.length) % items.length
          : (currentIndex - 1 + items.length) % items.length;
    items[nextIndex].focus();
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Créer une tâche le ${dateLabel}`}
      className="fixed z-[140] w-[252px] rounded-[4px] border border-[var(--color-border-success-subtle)] bg-white p-1 shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
      style={{ left: `${resolvedPosition.left}px`, top: `${resolvedPosition.top}px` }}
      onKeyDown={handleMenuKeyDown}
    >
      <div className="px-2.5 pb-1.5 pt-1">
        <p className="m-0 text-[10px] font-black uppercase tracking-[0.12em] text-accent-strong">Créer une tâche</p>
        <p className="m-0 mt-0.5 truncate text-[11px] font-semibold text-slate-500">{dateLabel}</p>
      </div>
      {actions.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          type="button"
          role="menuitem"
          className="flex min-h-9 w-full items-center gap-2 rounded-[4px] border border-transparent px-2.5 py-2 text-left text-xs font-black text-[var(--color-shell-action-soft-ink)] transition hover:border-[var(--color-border-success-muted)] hover:bg-accent-soft hover:text-[var(--color-shell-action-soft-ink-hover)] focus-visible:border-accent focus-visible:bg-accent-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
          onClick={() => {
            onClose?.();
            onSelect?.(key);
          }}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>,
    portalRoot,
  );
}
